/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */
import lottie from 'lottie-web'
import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'

export type LottieAnimationProps = {
    animationData: any
    loop: boolean
    autoplay: boolean
    stripExpressions?: boolean
    style?: React.CSSProperties
    className?: string
}

const stripExpressionOverrides = (data: any) => {
    if (!data || typeof data !== 'object') return data
    const clone = JSON.parse(JSON.stringify(data))
    const stack: any[] = [clone]

    while (stack.length) {
        const node = stack.pop()
        if (!node || typeof node !== 'object') continue

        for (const key of Object.keys(node)) {
            const value = node[key]

            if (typeof value === 'string' && ['x', 'y', 'z', 'rotation', 'scale'].includes(key)) {
                delete node[key]
                continue
            }

            if (value && typeof value === 'object') {
                stack.push(value)
            }
        }
    }

    return clone
}

export const LottieAnimation = React.forwardRef((props: LottieAnimationProps, ref) => {
    const animationContainer = useRef<HTMLDivElement>(null)
    const animationInstance = useRef<any>(null)
    const [totalDuration, setTotalDuration] = useState<number>(0)
    const sanitizedAnimationData = useMemo(
        () => (props.stripExpressions ? stripExpressionOverrides(props.animationData) : props.animationData),
        [props.animationData, props.stripExpressions]
    )

    useEffect(() => {
        if (animationContainer.current) {
            animationInstance.current = lottie.loadAnimation({
                container: animationContainer.current,
                loop: props.loop,
                autoplay: props.autoplay,
                animationData: sanitizedAnimationData,
            })
            setTotalDuration(animationInstance.current.getDuration(true))
        }
        return () => {
            animationInstance.current?.destroy()
        }
    }, [sanitizedAnimationData, props.loop, props.autoplay])

    /* Exposes the lottie instance methods to be called from the parent component */
    useImperativeHandle(ref, () => ({
        play: () => {
            animationInstance.current?.goToAndPlay(0, true)
        },
        reset: () => {
            animationInstance.current?.goToAndStop(0, true)
        },
        playAfterDelay: (delay: number) => {
            setTimeout(() => {
                animationInstance.current?.goToAndPlay(0, true)
            }, delay)
        },
        pause: () => {
            animationInstance.current?.pause()
        },
        // Evenly divides the animation into segments and plays the segment based on the segment number
        playSegment: (segmentNumber: number, totalSegments: number) => {
            if (totalDuration > 0) {
                const segments: number[][] = []
                const segmentLength = Math.floor(totalDuration / totalSegments)
                let currentFrame = 0
                for (let i = 0; i < 4; i++) {
                    segments.push([currentFrame, currentFrame + segmentLength])
                    currentFrame += segmentLength + 1
                }
                if (segmentNumber < segments.length) {
                    animationInstance.current?.playSegments(segments[segmentNumber], true)
                }
            }
        },
        // Given a keyframe name found in the animation data, plays the segment from the start of the keyframe to the end of the next keyframe
        playToKeyframe: (keyframe: string) => {
            const keyframes = getKeyframes(props.animationData)
            const keyframeStart = keyframes[keyframe]
            const keyframeKeys = Object.keys(keyframes)
            const keyframeIndex = keyframeKeys.indexOf(keyframe)
            const keyframeEnd =
                keyframeIndex < keyframeKeys.length - 1 ? keyframes[keyframeKeys[keyframeIndex + 1]] : totalDuration

            if (keyframeStart !== undefined) {
                animationInstance.current?.playSegments([keyframeStart, keyframeEnd], true)
            }
        }
    }))

    return <div ref={animationContainer} className={props.className} style={{ pointerEvents: 'none', ...props.style }} />
})

export const hasKeyframes = (animationData: any): boolean => {
    return !!(animationData?.markers && Array.isArray(animationData.markers) && animationData.markers.length > 0);
}

export const getKeyframes = (animationData: any): Record<string, number> => {
    if (hasKeyframes(animationData)) {
        const markers = animationData.markers
        const keyframes: Record<string, number> = {}
        for (const marker of markers) {
            const key = JSON.parse(marker.cm).name
            keyframes[key] = marker.tm
        }
        return keyframes
    }
    return {}
}
